import { apiClient } from "../axiosInstance";

class MessageService {
  constructor() {
    this.baseUrl = "/messages";
    this.adminBaseUrl = "/messages/admin/messages";
  }

  // ============================================
  // PUBLIC ENDPOINTS (No authentication required)
  // ============================================

  /**
   * Create a new contact message
   * @param {Object} data - Message data
   * @param {string} data.name - Sender name
   * @param {string} data.email - Sender email
   * @param {string} data.subject - Message subject
   * @param {string} data.content - Message content
   * @param {string} [data.phone] - Sender phone
   * @param {string} [data.company] - Company name
   * @param {string} [data.position] - Job position
   * @param {string} [data.category] - Message category
   * @param {string} [data.priority] - Message priority
   */
  async createMessage(data) {
    return apiClient.post(this.baseUrl, data);
  }

  // ============================================
  // ADMIN ENDPOINTS (Admin only)
  // ============================================

  /**
   * Get all messages with pagination and filters
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Items per page
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.priority] - Filter by priority
   * @param {string} [params.email] - Filter by email
   * @param {number} [params.assignedTo] - Filter by assigned admin
   * @param {string} [params.search] - Search query
   * @param {string} [params.fromDate] - Start date (ISO format)
   * @param {string} [params.toDate] - End date (ISO format)
   * @param {boolean} [params.isRead] - Filter by read status
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (ASC/DESC)
   */
  async getMessages(params = {}) {
    return apiClient.get(this.adminBaseUrl, { params });
  }

  /**
   * Get message statistics
   */
  async getMessageStats() {
    return apiClient.get(`${this.adminBaseUrl}/stats`);
  }

  /**
   * Get unassigned messages
   */
  async getUnassignedMessages() {
    return apiClient.get(`${this.adminBaseUrl}/unassigned`);
  }

  /**
   * Get overdue messages
   */
  async getOverdueMessages() {
    return apiClient.get(`${this.adminBaseUrl}/overdue`);
  }

  /**
   * Get recent messages
   * @param {number} [days=7] - Number of days
   * @param {number} [limit=10] - Number of messages to return
   */
  async getRecentMessages(days = 7, limit = 10) {
    return apiClient.get(`${this.adminBaseUrl}/recent`, {
      params: { days, limit },
    });
  }

  /**
   * Get messages by date range
   * @param {string} startDate - Start date (ISO format)
   * @param {string} endDate - End date (ISO format)
   */
  async getMessagesByDateRange(startDate, endDate) {
    return apiClient.get(`${this.adminBaseUrl}/date-range`, {
      params: { startDate, endDate },
    });
  }

  /**
   * Get messages needing attention
   */
  async getMessagesNeedingAttention() {
    return apiClient.get(`${this.adminBaseUrl}/attention`);
  }

  /**
   * Get message volume by day
   * @param {number} [days=30] - Number of days
   */
  async getVolumeByDay(days = 30) {
    return apiClient.get(`${this.adminBaseUrl}/volume`, { params: { days } });
  }

  /**
   * Get messages by source
   */
  async getMessagesBySource() {
    return apiClient.get(`${this.adminBaseUrl}/by-source`);
  }

  /**
   * Get average response time by category
   */
  async getAvgResponseTimeByCategory() {
    return apiClient.get(`${this.adminBaseUrl}/response-time`);
  }

  /**
   * Search messages
   * @param {Object} params - Search parameters
   * @param {string} params.q - Search query
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.priority] - Filter by priority
   * @param {string} [params.fromDate] - Start date
   * @param {string} [params.toDate] - End date
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Items per page
   */
  async searchMessages(params) {
    return apiClient.get(`${this.adminBaseUrl}/search`, { params });
  }

  /**
   * Filter messages with advanced criteria
   * @param {Object} filters - Filter criteria
   */
  async filterMessages(filters) {
    return apiClient.post(`${this.adminBaseUrl}/filter`, filters);
  }

  /**
   * Get conversation by email
   * @param {string} email - Email address
   * @param {number} [limit=10] - Number of messages to return
   */
  async getConversationByEmail(email, limit = 10) {
    return apiClient.get(`${this.adminBaseUrl}/conversation/${email}`, {
      params: { limit },
    });
  }

  /**
   * Get message by ID
   * @param {number} id - Message ID
   */
  async getMessageById(id) {
    return apiClient.get(`${this.adminBaseUrl}/${id}`);
  }

  /**
   * Get message by message number
   * @param {string} messageNumber - Message number
   */
  async getMessageByNumber(messageNumber) {
    return apiClient.get(`${this.adminBaseUrl}/number/${messageNumber}`);
  }

  /**
   * Get message by UUID
   * @param {string} uuid - Message UUID
   */
  async getMessageByUuid(uuid) {
    return apiClient.get(`${this.adminBaseUrl}/uuid/${uuid}`);
  }

  /**
   * Mark message as read
   * @param {number} id - Message ID
   */
  async markAsRead(id) {
    return apiClient.patch(`${this.adminBaseUrl}/${id}/read`);
  }

  /**
   * Mark message as unread
   * @param {number} id - Message ID
   */
  async markAsUnread(id) {
    return apiClient.patch(`${this.adminBaseUrl}/${id}/unread`);
  }

  /**
   * Mark message as spam
   * @param {number} id - Message ID
   */
  async markAsSpam(id) {
    return apiClient.post(`${this.adminBaseUrl}/${id}/spam`);
  }

  /**
   * Archive message
   * @param {number} id - Message ID
   */
  async archiveMessage(id) {
    return apiClient.post(`${this.adminBaseUrl}/${id}/archive`);
  }

  /**
   * Resolve message
   * @param {number} id - Message ID
   */
  async resolveMessage(id) {
    return apiClient.post(`${this.adminBaseUrl}/${id}/resolve`);
  }

  /**
   * Delete message
   * @param {number} id - Message ID
   */
  async deleteMessage(id) {
    return apiClient.delete(`${this.adminBaseUrl}/${id}`);
  }

  /**
   * Add admin response to message
   * @param {number} id - Message ID
   * @param {string} adminResponse - Response content
   */
  async addResponse(id, adminResponse) {
    return apiClient.post(`${this.adminBaseUrl}/${id}/respond`, {
      adminResponse,
    });
  }

  /**
   * Send response email to customer (Admin only)
   * @param {number} messageId - Message ID
   * @param {string} response - Response message
   * @param {string} customerEmail - Customer email address
   */
  async sendResponseEmail(messageId, response, customerEmail) {
    try {
      const responseData = await apiClient.post(
        `${this.adminBaseUrl}/${messageId}/send-response-email`,
        {
          response,
          customerEmail,
        },
      );
      return responseData.data;
    } catch (error) {
      console.error("Send response email error:", error);
      throw error;
    }
  }

  /**
   * Assign message to admin
   * @param {number} id - Message ID
   * @param {number} adminId - Admin ID
   */
  async assignMessage(id, adminId) {
    return apiClient.patch(`${this.adminBaseUrl}/${id}/assign`, { adminId });
  }

  /**
   * Forward message
   * @param {number} id - Message ID
   * @param {string} forwardedTo - Email address to forward to
   */
  async forwardMessage(id, forwardedTo) {
    return apiClient.post(`${this.adminBaseUrl}/${id}/forward`, {
      forwardedTo,
    });
  }

  /**
   * Add internal note to message
   * @param {number} id - Message ID
   * @param {string} note - Note content
   */
  async addInternalNote(id, note) {
    return apiClient.post(`${this.adminBaseUrl}/${id}/note`, { note });
  }

  // ============================================
  // BULK OPERATIONS (Admin only)
  // ============================================

  /**
   * Bulk update message status
   * @param {number[]} ids - Array of message IDs
   * @param {string} status - New status
   */
  async bulkUpdateStatus(ids, status) {
    return apiClient.patch(`${this.adminBaseUrl}/bulk-status`, { ids, status });
  }

  /**
   * Bulk assign messages to admin
   * @param {number[]} ids - Array of message IDs
   * @param {number} adminId - Admin ID
   */
  async bulkAssignToAdmin(ids, adminId) {
    return apiClient.patch(`${this.adminBaseUrl}/bulk-assign`, {
      ids,
      adminId,
    });
  }

  /**
   * Bulk mark messages as read
   * @param {number[]} ids - Array of message IDs
   */
  async bulkMarkAsRead(ids) {
    return apiClient.patch(`${this.adminBaseUrl}/bulk-read`, { ids });
  }

  /**
   * Bulk archive messages
   * @param {number[]} ids - Array of message IDs
   */
  async bulkArchive(ids) {
    return apiClient.patch(`${this.adminBaseUrl}/bulk-archive`, { ids });
  }

  /**
   * Bulk delete messages
   * @param {number[]} ids - Array of message IDs
   */
  async bulkDeleteMessages(ids) {
    return apiClient.delete(`${this.adminBaseUrl}/bulk-delete`, {
      data: { ids },
    });
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Get message status color for UI
   * @param {string} status - Message status
   * @returns {string} Tailwind CSS color class
   */
  getMessageStatusColor(status) {
    const colors = {
      new: "blue",
      read: "gray",
      replied: "green",
      forwarded: "purple",
      resolved: "green",
      archived: "gray",
      spam: "red",
    };
    return colors[status] || "gray";
  }

  /**
   * Get message status badge text
   * @param {string} status - Message status
   * @returns {string} Display text
   */
  getMessageStatusText(status) {
    const texts = {
      new: "New",
      read: "Read",
      replied: "Replied",
      forwarded: "Forwarded",
      resolved: "Resolved",
      archived: "Archived",
      spam: "Spam",
    };
    return texts[status] || status;
  }

  /**
   * Get message priority color for UI
   * @param {string} priority - Message priority
   * @returns {string} Tailwind CSS color class
   */
  getMessagePriorityColor(priority) {
    const colors = {
      low: "gray",
      normal: "blue",
      high: "orange",
      urgent: "red",
    };
    return colors[priority] || "gray";
  }

  /**
   * Get message category icon for UI
   * @param {string} category - Message category
   * @returns {string} Icon name
   */
  getMessageCategoryIcon(category) {
    const icons = {
      general: "HelpCircle",
      support: "Headphones",
      sales: "ShoppingBag",
      billing: "CreditCard",
      technical: "Settings",
      feedback: "MessageSquare",
      complaint: "AlertCircle",
      other: "Mail",
    };
    return icons[category] || "Mail";
  }

  /**
   * Format message time for display
   * @param {string} date - ISO date string
   * @returns {string} Human readable time
   */
  formatMessageTime(date) {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInSeconds = Math.floor(
      (now.getTime() - messageDate.getTime()) / 1000,
    );

    if (diffInSeconds < 60) {
      return "just now";
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    }

    return messageDate.toLocaleDateString();
  }

  /**
   * Check if message is overdue
   * @param {Object} message - Message object
   * @returns {boolean} True if overdue
   */
  isMessageOverdue(message) {
    if (message.status !== "new" && message.status !== "read") return false;

    const createdAt = new Date(message.createdAt);
    const hoursSinceCreation =
      (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
    return hoursSinceCreation > 24;
  }

  /**
   * Get message summary for dashboard
   * @returns {Promise} Message summary data
   */
  async getMessageSummary() {
    try {
      const [stats, unassigned, overdue] = await Promise.all([
        this.getMessageStats(),
        this.getUnassignedMessages(),
        this.getOverdueMessages(),
      ]);

      return {
        total: stats.data?.total || 0,
        unread: stats.data?.unread || 0,
        unassigned: unassigned.data?.messages?.length || 0,
        overdue: overdue.data?.messages?.length || 0,
        byCategory: stats.data?.byCategory || {},
        byPriority: stats.data?.byPriority || {},
      };
    } catch (error) {
      console.error("Failed to fetch message summary:", error);
      return {
        total: 0,
        unread: 0,
        unassigned: 0,
        overdue: 0,
        byCategory: {},
        byPriority: {},
      };
    }
  }

  /**
   * Export messages to CSV (utility method)
   * @param {Object} params - Filter parameters
   * @returns {Promise} CSV data
   */
  async exportMessagesToCSV(params = {}) {
    const response = await this.getMessages({ ...params, limit: 1000 });
    const messages = response.data?.messages || [];

    const headers = [
      "ID",
      "Number",
      "Name",
      "Email",
      "Subject",
      "Category",
      "Priority",
      "Status",
      "Created At",
    ];
    const rows = messages.map((msg) => [
      msg.id,
      msg.messageNumber,
      msg.name,
      msg.email,
      msg.subject,
      msg.category,
      msg.priority,
      msg.status,
      new Date(msg.createdAt).toLocaleString(),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    return csvContent;
  }
}

export const messageService = new MessageService();
